import type { DigitalTwinTranslations } from 'api/digitalTwin/digitalTwin.api'
import type {
  AppliedSettingSeries,
  AttributeChip,
} from 'helpers/appliedSettingValues.helper/appliedSettingValues.types'
import { toChartConfig, toChartItems } from 'helpers/appliedSettingValues.helper/chartProjection.helper'
import { selectSeries, selectUnitsWithOutcome } from 'helpers/appliedSettingValues.helper/seriesSelection.helper'
import {
  filterSeriesBySearchTerm,
  getTableSeries,
  mapDatasetsToTableData,
  toColumnGroups,
  toTableItems,
} from 'helpers/appliedSettingValues.helper/tableRows.helper'
import { type ReactNode, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { chartStore, clearGroup, generateNewGroupId } from 'store/chart/chart'
import EditableDataPointTable from 'ui/molecules/EditableDataPointTable/EditableDataPointTable'
import Chart from 'ui/uiConfig/components/Chart/Chart'
import { useSnapshot } from 'valtio'
import { AppliedSettingPropertyChips } from './AppliedSettingPropertyChips'

type AppliedSettingValuesPanelProps = {
  series: AppliedSettingSeries[]
  datasets: Dataset[]
  translations: DigitalTwinTranslations
  showGraph: boolean
  searchTerm: string
  onlyUnitsWithOutcome: boolean
  showPropertyChips: boolean
  attributeChips: AttributeChip[]
  selectedAttributes: string[]
  onSelectedAttributesChange: (attributes: string[]) => void
  hideEmptyColumns: boolean
}

export function AppliedSettingValuesPanel({
  series,
  datasets,
  translations,
  showGraph,
  searchTerm,
  onlyUnitsWithOutcome,
  showPropertyChips,
  attributeChips,
  selectedAttributes,
  onSelectedAttributesChange,
  hideEmptyColumns,
}: AppliedSettingValuesPanelProps): ReactNode {
  const { t } = useTranslation()
  const [groupId] = useState(generateNewGroupId)
  useEffect(() => () => clearGroup(groupId), [groupId])
  const settingTargets = series.flatMap((entry) => (entry.kind === 'setting' ? [entry.property] : []))
  const visibleSeries = selectSeries(series, settingTargets, selectedAttributes)
  const outcomeSeries = onlyUnitsWithOutcome ? selectUnitsWithOutcome(visibleSeries, datasets) : visibleSeries
  const chartSeries = filterSeriesBySearchTerm(outcomeSeries, searchTerm, t, translations)
  const { options } = toChartConfig(chartSeries)
  const chartConfig = {
    options: { ...options, plugins: { chartLegend: { groupId, shared: true }, syncYAxis: { enabled: false } } },
  }
  const { groupIdItems, groupIdHiddenReturnIds } = useSnapshot(chartStore)
  const legendReturnIds = new Set((groupIdItems[groupId] ?? []).map((item) => item.data_id))
  const hiddenReturnIds = groupIdHiddenReturnIds[groupId] ?? new Set<string>()
  const tableSeries = showGraph ? getTableSeries(outcomeSeries, legendReturnIds, hiddenReturnIds) : outcomeSeries
  const tableItems = toTableItems(tableSeries, translations)
  const value = mapDatasetsToTableData(datasets, tableItems, t, translations)
  const columnGroups = toColumnGroups(tableItems, t, translations)
  const areAllLegendSeriesHidden =
    showGraph && legendReturnIds.size > 0 && [...legendReturnIds].every((returnId) => hiddenReturnIds.has(returnId))
  const hasFailedDataset = datasets.some((dataset) => dataset.status === 'error')

  function handleToggleAttribute(attribute: string): void {
    onSelectedAttributesChange(
      selectedAttributes.includes(attribute)
        ? selectedAttributes.filter((selected) => selected !== attribute)
        : [...selectedAttributes, attribute]
    )
  }

  const propertyChips = attributeChips.length > 1 && showPropertyChips && (
    <AppliedSettingPropertyChips
      chips={attributeChips}
      selectedAttributes={selectedAttributes}
      onToggle={handleToggleAttribute}
      onClear={() => onSelectedAttributesChange([])}
    />
  )

  if (onlyUnitsWithOutcome && outcomeSeries.length === 0) {
    return (
      <div className="flex flex-col gap-2">
        {propertyChips}
        <div role="status" className="flex flex-col items-center py-6 text-center">
          <p className="m-0 max-w-xl text-grey-6">
            {t(
              'No unit has an outcome in the loaded period. Zooming changes the period. Untick "{{filterLabel}}" to show every unit.',
              {
                filterLabel: t('Filter by outcome'),
              }
            )}
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-2">
      {propertyChips}
      {showGraph && (
        <Chart
          items={toChartItems(chartSeries)}
          datasets={datasets}
          config={chartConfig}
          legendKey="appliedSettingValues"
        />
      )}
      {hasFailedDataset && <p role="alert">{t('Some applied setting values could not be loaded')}</p>}
      {areAllLegendSeriesHidden && <p className="italic">{t('All series are hidden in the graph')}</p>}
      {datasets.length ? (
        <EditableDataPointTable
          value={value}
          readOnly
          showActionButtons={false}
          emptyColumnFilterChecked={hideEmptyColumns}
          emptyColumnFilterMode="completelyEmpty"
          nameSearchTerm={searchTerm}
          hideNameSearch
          columnGroups={columnGroups}
        />
      ) : (
        <p className="italic">{t('No datasets found')}</p>
      )}
    </div>
  )
}
